import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAuthError, type Session } from '@supabase/supabase-js';
import { MIN_PASSWORD_LENGTH, newPasswordProblem, type AuthLink, type AuthLinkType } from '@cerebro-diem/core';
import { initialAuthLink } from '../../services/supabase';
import {
  forgetVerifiedLink,
  rememberVerifiedLink,
  verifiedLinkAllowsPasswordForm,
} from '../../services/verifiedLink';
import { useAuthStore } from '../../stores/authStore';
import type { VerifiedAccount } from '../../stores/createAuthStore';
import { errorMessage } from '../../services/errors';
import { authError, authInput, authNotice, authSubmit, tapLink } from '../../components/styles';
import AuthLayout from './AuthLayout';
import PasswordResetRequest from './PasswordResetRequest';

const EXPIRED = 'This link has expired or has already been used. Each link works once, and only the newest one works.';
const NOT_A_CEREBRO_LINK =
  "This isn't a link from a Cerebro invitation or reset email, so it was not used. Request a new link below.";

type Stage =
  | { name: 'checking' }
  | { name: 'confirm'; tokenHash: string; type: AuthLinkType }
  | { name: 'form'; account: VerifiedAccount; type: AuthLinkType | null; switchedFrom: string | null }
  | { name: 'link-failed'; message: string }
  | { name: 'no-link' }
  | { name: 'done'; email: string; type: AuthLinkType | null };

/** Where the page starts, once the stored session has loaded. */
function startingStage(link: AuthLink | null, session: Session | null): Stage {
  if (link?.kind === 'error') {
    return {
      name: 'link-failed',
      message: link.code === 'otp_expired' ? EXPIRED : `Supabase rejected this link: ${link.description}`,
    };
  }
  if (link?.kind === 'session_in_hash') return { name: 'link-failed', message: NOT_A_CEREBRO_LINK };
  if (link?.kind === 'token_hash') return { name: 'confirm', tokenHash: link.tokenHash, type: link.type };
  // No link: only a reload right after this tab verified one keeps the form.
  const user = session?.user ?? null;
  if (user?.email && verifiedLinkAllowsPasswordForm(user.id)) {
    return { name: 'form', account: { id: user.id, email: user.email }, type: null, switchedFrom: null };
  }
  return { name: 'no-link' };
}

function verifyFailure(err: unknown): string {
  return isAuthError(err) && err.code === 'otp_expired'
    ? EXPIRED
    : `Supabase could not verify this link: ${errorMessage(err)}`;
}

const TITLES: Record<AuthLinkType | 'none', { title: string; subtitle: string }> = {
  invite: { title: 'Welcome to Cerebro Diem', subtitle: 'Choose a password to finish setting up your account.' },
  recovery: { title: 'Set a new password', subtitle: 'Choose a new password for your account.' },
  none: { title: 'Set your password', subtitle: 'Choose a new password for your account.' },
};

function BackToSignIn() {
  return (
    <p className="mt-6 text-center">
      <Link to="/login" className={`${tapLink} text-primary font-medium hover:underline`}>
        Back to sign in
      </Link>
    </p>
  );
}

/**
 * Where an invite or password-reset email lands. Continue verifies the link and
 * signs in its account; the page then names that account and lets it choose a password.
 */
export default function SetPasswordPage() {
  const navigate = useNavigate();
  const { session, isLoading, verifyEmailLink, setPassword } = useAuthStore();
  const [stage, setStage] = useState<Stage>({ name: 'checking' });
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  useEffect(() => {
    if (isLoading || stage.name !== 'checking') return;
    try {
      setStage(startingStage(initialAuthLink, session));
    } catch (err) {
      // sessionStorage can be blocked by the browser.
      setStage({ name: 'link-failed', message: `This page could not start: ${errorMessage(err)}` });
    }
  }, [isLoading, session, stage.name]);

  const handleContinue = async () => {
    if (stage.name !== 'confirm') return;
    const previous = session?.user ?? null;
    setError('');
    setIsWorking(true);
    try {
      let account: VerifiedAccount;
      try {
        // Spends the link. A person has to press Continue, so a mail scanner cannot.
        account = await verifyEmailLink(stage.tokenHash, stage.type);
      } catch (err) {
        setStage({ name: 'link-failed', message: verifyFailure(err) });
        return;
      }
      rememberVerifiedLink(account.id);
      navigate('/set-password', { replace: true }); // the spent token leaves the address bar
      setStage({
        name: 'form',
        account,
        type: stage.type,
        switchedFrom: previous && previous.id !== account.id ? (previous.email ?? 'a different account') : null,
      });
    } catch (err) {
      setError(`Could not continue: ${errorMessage(err)}`);
    } finally {
      setIsWorking(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (stage.name !== 'form') return;
    const problem = newPasswordProblem(newPassword, confirmation);
    if (problem) {
      setError(problem);
      return;
    }
    setError('');
    setIsWorking(true);
    try {
      await setPassword(newPassword, stage.account.id);
      forgetVerifiedLink();
      setStage({ name: 'done', email: stage.account.email, type: stage.type });
    } catch (err) {
      setError(`Your password was not saved: ${errorMessage(err)}`);
    } finally {
      setIsWorking(false);
    }
  };

  if (stage.name === 'checking') {
    return (
      <AuthLayout title="Set your password">
        <div className="flex flex-col items-center gap-3" role="status">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
          <p className="text-sm text-on-surface-variant">Checking your link...</p>
        </div>
      </AuthLayout>
    );
  }

  if (stage.name === 'link-failed') {
    return (
      <AuthLayout title="This link didn't work">
        <div className={`${authError} mb-4`} role="alert">
          {stage.message}
        </div>
        <p className="mb-4 text-sm text-on-surface-variant">
          Enter your email and we'll send you a new link to set your password.
        </p>
        <PasswordResetRequest />
        <BackToSignIn />
      </AuthLayout>
    );
  }

  if (stage.name === 'no-link') {
    return (
      <AuthLayout title="Set your password" subtitle="Open the link in your invitation or password reset email.">
        <p className="mb-4 text-sm text-on-surface-variant">
          No email, or the link no longer works? Enter your email to get a new link.
        </p>
        <PasswordResetRequest />
        <BackToSignIn />
      </AuthLayout>
    );
  }

  if (stage.name === 'confirm') {
    const signedInEmail = session?.user.email ?? null;
    return (
      <AuthLayout title={TITLES[stage.type].title} subtitle="Continue to open your account, then choose a password.">
        <div className="space-y-4">
          {signedInEmail && (
            <div className={authNotice}>
              This browser is signed in as <strong>{signedInEmail}</strong>. Continuing signs you in to the
              account this link was sent to instead.
            </div>
          )}
          {error && (
            <div className={authError} role="alert">
              {error}
            </div>
          )}
          <button type="button" onClick={handleContinue} disabled={isWorking} className={authSubmit}>
            {isWorking ? 'Opening your account...' : 'Continue'}
          </button>
        </div>
        <BackToSignIn />
      </AuthLayout>
    );
  }

  if (stage.name === 'done') {
    return (
      <AuthLayout title="Password saved" subtitle={`You're signed in as ${stage.email}.`}>
        <div className={`${authNotice} mb-4`}>
          {stage.type === 'invite'
            ? 'On iPhone: in Safari, tap Share, then Add to Home Screen. The Home Screen app keeps its own sign-in, so sign in there once with this email and your new password.'
            : 'Use your new password the next time you sign in, on any device.'}
        </div>
        <button type="button" onClick={() => navigate('/', { replace: true })} className={authSubmit}>
          Open Cerebro
        </button>
      </AuthLayout>
    );
  }

  const { title, subtitle } = TITLES[stage.type ?? 'none'];
  return (
    <AuthLayout title={title} subtitle={subtitle}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {stage.switchedFrom && (
          <div className={authNotice} role="status">
            You were signed in as <strong>{stage.switchedFrom}</strong>. This link is for{' '}
            <strong>{stage.account.email}</strong>, so this browser is now signed in as{' '}
            <strong>{stage.account.email}</strong>.
          </div>
        )}
        {error && (
          <div className={authError} role="alert">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="account-email" className="block text-sm font-medium mb-2">
            Setting the password for
          </label>
          {/* Read-only, and also lets the password manager save the new password under this account. */}
          <input
            id="account-email"
            type="email"
            autoComplete="username"
            value={stage.account.email}
            readOnly
            className={`${authInput} text-lg font-semibold`}
          />
        </div>

        <div>
          <label htmlFor="new-password" className="block text-sm font-medium mb-2">
            New password
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={authInput}
            aria-describedby="new-password-hint"
            required
          />
          <p id="new-password-hint" className="mt-1 text-xs text-on-surface-variant">
            At least {MIN_PASSWORD_LENGTH} characters.
          </p>
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium mb-2">
            Confirm password
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            className={authInput}
            required
          />
        </div>

        <button type="submit" disabled={isWorking} className={authSubmit}>
          {isWorking ? 'Saving...' : 'Save password'}
        </button>
      </form>
    </AuthLayout>
  );
}
